import {
  useId,
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { Icon, type IconName } from '../Icon/Icon';
import { liftOnFocus } from '../../chrome/keyboardLift';
import './field.css';

interface FieldBase {
  /** Visible label. Without one, pass aria-label on the input. */
  label?: ReactNode;
  /** Inline error, shown in place under the input (design rule 6). */
  error?: ReactNode;
  /** Help line under the input. */
  hint?: ReactNode;
  /** Leading icon (S1's plane); omit for a plain input. */
  icon?: IconName;
  /** Departure-board type for codes (flight number, booking reference). */
  mono?: boolean;
  /** Colours the icon once a value is picked. */
  filled?: boolean;
  /** Raise the field above the keyboard on focus (rule 7). Default true. */
  lift?: boolean;
  /** Something outside the field that must also clear the keyboard (the S2 calendar). */
  liftExtra?: () => HTMLElement | null;
  /** A trailing control inside the input (S2's calendar button). */
  trailing?: ReactNode;
  /** Rendered inside the positioned field, after the input: e.g. a suggestion list
   *  (give it `data-lift-extra` so the lift keeps it above the keyboard). */
  children?: ReactNode;
  className?: string;
}

function useFieldIds(id: string | undefined, error: ReactNode, hint: ReactNode) {
  const auto = useId();
  const inputId = id ?? `f${auto}`;
  const errorId = error ? `${inputId}-err` : undefined;
  const hintId = hint ? `${inputId}-hint` : undefined;
  return { inputId, errorId, hintId, describedBy: [errorId, hintId].filter(Boolean).join(' ') || undefined };
}

function FieldShell({
  base,
  ids,
  control,
}: {
  base: FieldBase;
  ids: ReturnType<typeof useFieldIds>;
  control: ReactNode;
}) {
  const { label, error, hint, icon, filled, trailing, children, className } = base;
  const cls = ['field', filled && 'is-filled', trailing && 'field--trailing', className]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      {label && (
        <label className="field__label" htmlFor={ids.inputId}>
          {label}
        </label>
      )}
      <div className="field__control">
        {control}
        {icon && <Icon name={icon} className="field__icon" />}
        {trailing}
      </div>
      {children}
      {error && (
        <p className="hint hint--warn" id={ids.errorId} role="alert">
          {error}
        </p>
      )}
      {hint && (
        <p className="hint" id={ids.hintId}>
          {hint}
        </p>
      )}
    </div>
  );
}

function useLiftFocus<E extends HTMLElement>(base: FieldBase, onFocus?: (e: FocusEvent<E>) => void) {
  return (e: FocusEvent<E>) => {
    if (base.lift !== false) liftOnFocus(e, base.liftExtra?.());
    onFocus?.(e);
  };
}

export type TextFieldProps = FieldBase & Omit<InputHTMLAttributes<HTMLInputElement>, 'children'>;

/** Label + input + inline error/hint, with the keyboard lift built in. */
export function TextField(props: TextFieldProps) {
  const {
    label,
    error,
    hint,
    icon,
    mono,
    filled,
    lift,
    liftExtra,
    trailing,
    children,
    className,
    id,
    onFocus,
    ...input
  } = props;
  const base = { label, error, hint, icon, mono, filled, lift, liftExtra, trailing, children, className };
  const ids = useFieldIds(id, error, hint);
  const handleFocus = useLiftFocus<HTMLInputElement>(base, onFocus);
  const cls = ['input', !icon && 'input--plain', mono && 'mono'].filter(Boolean).join(' ');
  return (
    <FieldShell
      base={base}
      ids={ids}
      control={
        <input
          type="text"
          {...input}
          id={ids.inputId}
          className={cls}
          aria-invalid={error ? true : undefined}
          aria-describedby={ids.describedBy}
          onFocus={handleFocus}
        />
      }
    />
  );
}

export type TextAreaFieldProps = Omit<FieldBase, 'icon' | 'trailing'> &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'children'>;

/** Multi-line variant (S13's "Anything else?"). */
export function TextAreaField(props: TextAreaFieldProps) {
  const { label, error, hint, mono, filled, lift, liftExtra, children, className, id, onFocus, ...ta } =
    props;
  const base = { label, error, hint, mono, filled, lift, liftExtra, children, className };
  const ids = useFieldIds(id, error, hint);
  const handleFocus = useLiftFocus<HTMLTextAreaElement>(base, onFocus);
  return (
    <FieldShell
      base={base}
      ids={ids}
      control={
        <textarea
          rows={4}
          {...ta}
          id={ids.inputId}
          className={`input input--plain${mono ? ' mono' : ''}`}
          aria-invalid={error ? true : undefined}
          aria-describedby={ids.describedBy}
          onFocus={handleFocus}
        />
      }
    />
  );
}
